/* eslint-disable no-underscore-dangle */
import '@skyscanner-internal/backpack-web/bpk-stylesheets';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/larken';
import { afterDomContentLoaded } from 'saddlebag-browser';
import logger from 'saddlebag-logger';
import { initTrackingPreferences } from 'saddlebag-tracking-preferences';
import { isLoggedIn } from 'saddlebag-user-management';

import './public-path';
import './bootstrap/error-handling';
import cultureSelectorInit from './bootstrap/culture-selector';
import { ENTRYPOINTS } from './constants';
import { loadExternalScripts } from './scriptLoader';
import { isConsentEntrypoint } from './utils/entrypoint';
import { redirectFromQueryString } from './utils/urls';

const initTracking = () => {
  const {
    consentIdServiceUrl = '/g/purpose-scoped-id-service/identifiers/consent-id',
    enableConsentIdService = false,
    enableCookieVersionRollback = false,
    enableKetchCMP = false,
    enableKetchPsidSync = false,
    enableLegacyConsentSync = false,
    enablePurposeScopedIdExistingGDPRUsers = false,
    enablePurposeScopedIdNonRegulatedMarkets = false,
    enablePurposeScopedIdService = false,
    ketchConsentCookieName = '_swb_consent_',
    ketchScriptUrl = '',
    ketchTCFStorageKey = '_ketch_tcf_tcdata_v1_',
    market,
    purposeScopedIdServiceKetchCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh',
    purposeScopedIdServiceLegacyCmpUrl = '/g/purpose-scoped-id-service/identifiers/refresh-legacy',
  } = window.__internal || {};

  initTrackingPreferences({
    consumer: 'login',
    consentIdServiceUrl,
    enableConsentIdService,
    enableCookieVersionRollback,
    enableKetchCMP,
    enableKetchPsidSync,
    enableLegacyConsentSync,
    enablePurposeScopedIdExistingGDPRUsers,
    enablePurposeScopedIdNonRegulatedMarkets,
    enablePurposeScopedIdService,
    ketchScriptUrl,
    ketchConsentCookieName,
    ketchTCFStorageKey,
    market,
    purposeScopedIdServiceKetchCmpUrl,
    purposeScopedIdServiceLegacyCmpUrl,
  });
};

const mountConsentComponent = async (entrypoint) => {
  try {
    const { props = {} } = window.__internal || {};

    const appRoot = document.getElementById('app-root');
    if (!appRoot) {
      throw new Error('#app-root element not found');
    }

    // Imported here, not at the top of the file: react/react-dom are
    // globals loaded by loadExternalScripts, not bundled. A static import
    // would run too early and throw.
    const { createElement } = await import('react');
    const { createRoot } = await import('react-dom/client');

    const importComponentByEntrypoint = {
      [ENTRYPOINTS.SUBSCRIPTION_SUBSCRIBE]: () =>
        import('./components/Subscriptions/Subscribe'),
      [ENTRYPOINTS.SUBSCRIPTION_UNSUBSCRIBE]: () =>
        import('./components/Subscriptions/Unsubscribe'),
      [ENTRYPOINTS.SUBSCRIPTION_FAILED]: () =>
        import('./components/Subscriptions/Failed'),
    };
    const importComponent = importComponentByEntrypoint[entrypoint];
    if (!importComponent) {
      throw new Error(
        `Unrecognised entrypoint for consent component: ${entrypoint}`,
      );
    }
    const { default: Component } = await importComponent();

    const root = createRoot(appRoot);
    root.render(createElement(Component, props));
  } catch (err) {
    logger.logError({
      message: `Failed to mount consent component ${err}`,
      component: 'index',
    });
  }
};

(async () => {
  try {
    await loadExternalScripts();
  } catch (missing) {
    logger.logError({
      message: `Failed to load dependencies ${missing}`,
      component: 'index',
    });
  }

  try {
    initTracking();

    afterDomContentLoaded(async () => {
      const { default: hydrate } = await import('./hydrate');
      hydrate();

      // Consent skips the login gate below - running it there would
      // redirect a logged-in traveller away before the page renders.
      const { entrypoint } = window.__internal || {};

      if (isConsentEntrypoint(entrypoint)) {
        cultureSelectorInit();

        // Lazy-loaded for the same reason as mountConsentComponent - needs
        // react/react-dom loaded first.
        const { default: debugControlsInit } =
          await import('./bootstrap/debug');
        debugControlsInit();
        await mountConsentComponent(entrypoint);
        return;
      }

      if (await isLoggedIn()) {
        redirectFromQueryString(window);
      }
    });
  } catch (err) {
    logger.logError({
      message: `Failed to initialise client side ${err}`,
      component: 'index',
    });
  }
})();
